import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, ListChecks, FileSpreadsheet, Sparkles, MessageSquare,
  HelpCircle, Languages, CreditCard, ArrowRight, Share2, Flame, Trophy } from "lucide-react";
import { toast } from "sonner";
import ProgressCardModal from "./ProgressCardModal";

const TILES = [
  { to: "/tutor/worksheet",  icon: FileText,        label: "Worksheet",         desc: "Practice questions + answer key PDF" },
  { to: "/tutor/quiz",       icon: ListChecks,      label: "Quiz",              desc: "Auto-gradable MCQ / T-F / fill" },
  { to: "/tutor/test",       icon: FileSpreadsheet, label: "Unit / Final test", desc: "Full-length, multi-section test" },
  { to: "/tutor/study-plan", icon: Sparkles,        label: "Study plan",        desc: "Personalised week-by-week plan" },
  { to: "/tutor/homework",   icon: HelpCircle,      label: "Homework helper",   desc: "Hints — guides you to the answer" },
  { to: "/tutor/chat",       icon: MessageSquare,   label: "AI Tutor chat",     desc: "Full explanations, worked examples" },
  { to: "/tutor/english",    icon: Languages,       label: "English center",    desc: "Vocab, reading, writing, grammar" },
  { to: "/tutor/french",     icon: Languages,       label: "French center",     desc: "Vocab, lecture, écriture, grammaire" },
];

export default function TutorDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    api.get("/api/tutor/dashboard").then(({ data }) => {
      if (!data.profile.onboarded) { nav("/tutor/onboarding", { replace: true }); return; }
      setData(data);
    }).catch(() => nav("/login"));
  }, [nav]);

  if (!data) return <div className="p-6 text-sm text-[#4B5563]" data-testid="tutor-loading">Loading…</div>;

  const sub = data.subscription;
  const planName = sub ? (sub.plan === "premium_unlimited" ? "Premium Unlimited" : sub.plan) : "Free Forever";
  const isPremium = !!sub && sub.plan === "premium_unlimited";
  const limits = data.free_daily_limits || {};
  const used = data.daily_used || {};
  const streak = data.streak || 0;
  const ach = data.achievements_summary || { earned_count: 0, total: 0, recent: [] };

  return (
    <div data-testid="tutor-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Teacher4Me</div>
          <h1 className="font-display font-black text-3xl md:text-4xl mt-1">
            Hi {data.profile.parent_name || "there"}! Welcome back.
          </h1>
          <p className="text-[#4B5563] mt-1 text-sm">
            {data.profile.grade} · {data.profile.province} · {data.profile.subjects.length} subjects
          </p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl px-4 py-3 text-sm">
          <div className="text-[#4B5563] text-xs">Current plan</div>
          <div className="font-display font-bold capitalize text-[#064E3B]" data-testid="tutor-plan-name">{planName}</div>
          {!isPremium && (
            <Link to="/tutor/pricing" data-testid="tutor-upgrade" className="btn-primary text-xs mt-2 inline-flex items-center gap-1">
              <CreditCard className="w-3 h-3" /> Upgrade $9.99
            </Link>
          )}
          <button data-testid="tutor-share-card" onClick={() => setShowCard(true)}
            className="btn-outline text-xs mt-2 inline-flex items-center gap-1 w-full justify-center">
            <Share2 className="w-3 h-3" /> Share progress
          </button>
          {data.profile.parent_email && (
            <label className="flex items-start gap-1.5 mt-2 cursor-pointer text-[10px] text-[#4B5563]">
              <input type="checkbox" data-testid="toggle-parent-digest"
                defaultChecked={data.profile.parent_digest_opt_in !== false}
                onChange={async (e) => {
                  try {
                    await api.post("/api/tutor/profile/digest-pref",
                      { parent_digest_opt_in: e.target.checked });
                  } catch (err) {
                    console.error("digest pref toggle failed", err);
                  }
                }}
                className="mt-0.5" />
              <span>Email parents a Sunday recap</span>
            </label>
          )}
        </div>
      </div>

      <ProgressCardModal open={showCard} onClose={() => setShowCard(false)} />

      {/* Daily usage / streak / badges strip */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="dash-streak">
          <div className="flex items-center gap-2 text-[#D92D20]">
            <Flame className="w-5 h-5" />
            <div className="text-xs uppercase tracking-widest font-bold">Learning streak</div>
          </div>
          <div className="font-display font-black text-4xl mt-2">{streak} <span className="text-base text-[#4B5563]">day{streak === 1 ? "" : "s"}</span></div>
          <div className="text-xs text-[#4B5563] mt-1">
            {streak === 0 ? "Use any tile today to start a streak." : streak < 3 ? "Keep going — 3-day streak unlocks a badge." : streak < 7 ? "Almost at a 7-day badge!" : "You're on fire!"}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid="dash-badges">
          <div className="flex items-center gap-2 text-[#064E3B]">
            <Trophy className="w-5 h-5" />
            <div className="text-xs uppercase tracking-widest font-bold">Achievements</div>
          </div>
          <div className="font-display font-black text-4xl mt-2">{ach.earned_count}<span className="text-base text-[#4B5563]">/{ach.total}</span></div>
          <div className="flex flex-wrap gap-1 mt-2 min-h-[24px]">
            {ach.recent.map((a) => (
              <span key={a.key} title={a.name} className="text-xl">{a.icon}</span>
            ))}
            {ach.recent.length === 0 && <span className="text-xs text-[#4B5563]">No badges yet — start a quiz or worksheet!</span>}
          </div>
        </div>
        {!isPremium ? (
          <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-2xl p-5" data-testid="dash-daily-usage">
            <div className="text-xs uppercase tracking-widest font-bold text-[#D92D20]">Today&apos;s free usage</div>
            <ul className="mt-2 text-sm space-y-1">
              {[
                ["AI Tutor questions", "tutor_questions"],
                ["Worksheets", "worksheets"],
                ["Quizzes", "quizzes"],
              ].map(([label, k]) => {
                const u = used[k] || 0, lim = limits[k] || 0;
                const pct = lim ? Math.min(100, Math.round(100 * u / lim)) : 0;
                return (
                  <li key={k}>
                    <div className="flex justify-between text-xs"><span>{label}</span><b>{u}/{lim}</b></div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden mt-0.5">
                      <div className="h-full bg-[#D92D20]" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
            <Link to="/tutor/pricing" className="text-xs text-[#D92D20] font-semibold mt-2 inline-block">
              Go unlimited · $9.99/mo →
            </Link>
          </div>
        ) : (
          <div className="bg-[#D1FAE5] border border-[#A7F3D0] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-[#064E3B]">Premium Unlimited</div>
            <p className="text-sm mt-2 text-[#064E3B]">No daily caps — generate as much as your student needs.</p>
            <Link to="/tutor/achievements" className="text-xs text-[#064E3B] font-semibold mt-2 inline-block">View all badges →</Link>
          </div>
        )}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((t) => (
          <Link to={t.to} key={t.to} data-testid={`tile-${t.to.replace(/\//g, "-")}`}
            className="group bg-white border border-[#E5E7EB] rounded-2xl p-5 card-lift">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FEF2F2] text-[#D92D20] flex items-center justify-center">
                <t.icon className="w-5 h-5" />
              </div>
              <div className="font-display font-bold text-lg">{t.label}</div>
            </div>
            <p className="text-sm text-[#4B5563] mt-2">{t.desc}</p>
            <div className="text-sm font-semibold text-[#D92D20] mt-3 inline-flex items-center gap-1 group-hover:underline">
              Open <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <div className="font-display font-bold text-lg">Recent activity</div>
          {data.recent.length === 0 && (
            <div className="text-sm text-[#4B5563] mt-3" data-testid="tutor-recent-empty">
              You haven&apos;t generated anything yet — pick a tile above to get started.
            </div>
          )}
          <ul className="mt-3 divide-y divide-[#E5E7EB]" data-testid="tutor-recent-list">
            {data.recent.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold capitalize text-sm truncate">{r.kind} · {r.meta?.topic || r.meta?.subject || ""}</div>
                  <div className="text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <Link to={`/tutor/items/${r.id}`} data-testid={`recent-${r.id}`}
                  className="text-sm font-semibold text-[#D92D20] hover:underline">Open</Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <div className="font-display font-bold text-lg">By the numbers</div>
          <ul className="mt-3 text-sm space-y-2" data-testid="tutor-counts">
            {Object.entries(data.counts).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="capitalize text-[#4B5563]">{k.replace("_", " ")}</span>
                <span className="font-display font-bold">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
